import React, { useState } from 'react';
import { AlertTriangle, Trash2, CheckCircle2 } from 'lucide-react';
import { Modal } from './Modal';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  title: string;
  description: string;
  confirmWord?: string; // If set, user must type this exact word
  confirmButtonText?: string;
  variant?: 'danger' | 'warning' | 'primary';
  loading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmWord,
  confirmButtonText = 'Confirm',
  variant = 'danger',
  loading = false
}) => {
  const [typedWord, setTypedWord] = useState('');
  const canConfirm = !confirmWord || typedWord.trim().toUpperCase() === confirmWord.toUpperCase();

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm();
    setTypedWord('');
  };

  const getVariantIcon = () => {
    switch (variant) {
      case 'danger':
        return <Trash2 className="w-6 h-6 text-rose-400" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-amber-400" />;
      default:
        return <CheckCircle2 className="w-6 h-6 text-brand-400" />;
    }
  };

  const getButtonClass = () => {
    if (!canConfirm || loading) {
      return 'opacity-50 cursor-not-allowed bg-gray-600 text-gray-300';
    }
    switch (variant) {
      case 'danger':
        return 'bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-900/30';
      case 'warning':
        return 'bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/30';
      default:
        return 'bg-brand-500 hover:bg-brand-400 text-forest-void font-bold shadow-glow-sm';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="md">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-2xl bg-[#22493c] dark:bg-[#22493c] light:bg-gray-100 shrink-0">
          {getVariantIcon()}
        </div>
        <div>
          <p className="text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] leading-relaxed">
            {description}
          </p>

          {confirmWord && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                Type <span className="font-bold text-rose-400 uppercase tracking-wider">{confirmWord}</span> to confirm:
              </label>
              <input
                type="text"
                value={typedWord}
                onChange={(e) => setTypedWord(e.target.value)}
                placeholder={confirmWord}
                className="w-full px-3.5 py-2 rounded-xl text-sm outline-none transition-colors border
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE]">
        <button
          type="button"
          onClick={() => {
            setTypedWord('');
            onClose();
          }}
          disabled={loading}
          className="px-4 py-2 rounded-full text-sm font-medium transition-colors
            bg-[#22493c] text-white hover:bg-[#2a5547]
            light:bg-gray-100 light:text-gray-700 light:hover:bg-gray-200"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm || loading}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${getButtonClass()}`}
        >
          {loading ? 'Processing...' : confirmButtonText}
        </button>
      </div>
    </Modal>
  );
};
